import { useState } from "react";
import { Download, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AdminLayout, Panel } from "@/components/admin/AdminLayout";

const SECTIONS = [
  {
    id: "half", title: "Half-page (2 per sheet)", cut: "Cut along the dashed line across the middle.",
    items: [
      { kind: "color", label: "Color PDF", sub: "For color printers", file: "jimmyz_restoz_flyer_color.pdf" },
      { kind: "bw", label: "Black & White PDF", sub: "Saves ink on any printer", file: "jimmyz_restoz_flyer_bw.pdf" },
    ],
  },
  {
    id: "quarter", title: "Quarter-page (4 per sheet)", cut: "Cut along both dashed lines.",
    items: [
      { kind: "quarter_color", label: "Color PDF", sub: "For color printers", file: "jimmyz_restoz_quarter_color.pdf" },
      { kind: "quarter_bw", label: "Black & White PDF", sub: "Saves ink on any printer", file: "jimmyz_restoz_quarter_bw.pdf" },
    ],
  },
];

const FlyerButton = ({ kind, label, sub, file }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/flyers/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = Object.assign(document.createElement("a"), { href: url, download: file });
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't download the flyer. Please try again."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      className="flex min-h-[96px] w-full items-center gap-4 rounded-lg bg-[#FFD23F] px-5 text-left text-[#121214] transition-transform hover:-translate-y-0.5 disabled:opacity-70"
      data-testid={`flyer-download-${kind}-button`}
    >
      {busy ? <Loader2 className="h-8 w-8 shrink-0 animate-spin" /> : <Download className="h-8 w-8 shrink-0" strokeWidth={2.2} />}
      <span>
        <span className="font-sub block text-xl font-semibold uppercase tracking-wide">{label}</span>
        <span className="block text-sm">{sub}</span>
      </span>
    </button>
  );
};

export default function FlyersPage() {
  return (
    <AdminLayout title="Flyers" testId="admin-flyers-page">
      <p className="mb-5 flex items-start gap-3 text-zinc-300" data-testid="flyers-help-text">
        <Printer className="mt-0.5 h-5 w-5 shrink-0 text-[#FFD23F]" />
        Print on regular letter paper at 100% (actual size), not "Fit to page".
      </p>
      {SECTIONS.map((s) => (
        <Panel key={s.id} title={s.title} testId={`flyers-section-${s.id}`}>
          <p className="mb-4 text-sm text-zinc-400">{s.cut}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {s.items.map((f) => <FlyerButton key={f.kind} {...f} />)}
          </div>
        </Panel>
      ))}
    </AdminLayout>
  );
}
